import {
  createMultimediaVideoAttachment,
  deleteMultimediaRequest,
  getVideoProcessingStatus,
  updateNameRequest,
  updatePictureRequest,
  uploadMultimediaLink,
} from '../../../api/multimedia/multimediaApi'
import {
  changeAttachmentOrder,
  changeAttachmentTitle,
  changeFloorplanStatus,
  deleteAttachmentSuccess,
  setPictureOrder,
  updatePictureSuccess,
  updateVideoInfo,
  uploadAttachmentLink,
  uploadAttachmentSuccess,
} from '../../reducers/multimedia/multimediaFeature'
import { DispatchType } from '../../../@types/redux-types'
import { MultimediaUploadResponse, PluploadResponse, UploadFile } from '../../../@types/multimedia-types'
import { Attachment, AttachmentType, VideoInfoProcess } from '../../../@types/types'
import { showMessage } from '../../reducers/message/messageFeature'
import { insertionChanged } from '../../reducers/insertion/insertionFeature'
import { tracking } from '../../../tracking/tracking'
import { InitState } from '../../reducers/root/initialState'
import { getDefaultLogger } from '../../../services/logger/logger'
import { AppDispatch } from '../../../@types/store'
import { UniqueIdentifier } from '@dnd-kit/core'

const UPDATE_VIDEO_PROGRESS_POLL_INTERVAL = 500
export const deleteAttachment =
  (id: string, source?: string, size?: number, isVideo: boolean = false) =>
  async (dispatch: DispatchType, getState: () => InitState) => {
    try {
      const requiresMultimediaOperation = getState().insertionFeature.insertionIdentification.realEstateId !== undefined
      if (!requiresMultimediaOperation || isVideo) {
        await deleteMultimediaRequest(id, source, size)
      }
      tracking({
        eventName: 'attachment_delete_button_click',
        flowInformation: getState().flowFeature.flowInformation,
        entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
      })
      dispatch(insertionChanged())
      dispatch(deleteAttachmentSuccess({ id: id, moveToAttachmentsToDelete: requiresMultimediaOperation }))
    } catch {
      dispatch(showMessage({ message: 'unknown' }))
    }
  }

export const finishUpload = (file: UploadFile, response: PluploadResponse) => (dispatch: DispatchType) => {
  const upResponse: MultimediaUploadResponse = JSON.parse(response.response)
  const attachment: Attachment = {
    id: upResponse.id,
    url: upResponse.url,
    bucketName: upResponse.bucketName,
    contentType: upResponse.contentType,
    document: upResponse.document,
    picture: upResponse.picture,
    size: upResponse.size,
    source: upResponse.source,
    title: upResponse.title,
    floorplan: upResponse.floorplan,
    type: upResponse.picture ? AttachmentType.PICTURE : AttachmentType.DOCUMENT,
  }
  dispatch(insertionChanged())
  dispatch(uploadAttachmentSuccess({ fileId: file.id, attachment: attachment }))
}
export const finishVideoUpload = (file: UploadFile, realEstateId: number) => async (dispatch: AppDispatch) => {
  try {
    const attachment = await createMultimediaVideoAttachment(file.videoId!, realEstateId)

    await loopVideoProcessingStatus(realEstateId, attachment.offerApiId!, dispatch)

    dispatch(insertionChanged())
    dispatch(uploadAttachmentSuccess({ fileId: file.id, attachment: attachment }))
  } catch (error) {
    getDefaultLogger().warn('Video upload failed!', error)
  }
}

async function loopVideoProcessingStatus(
  realEstateId: number,
  offerApiId: string,
  dispatch: AppDispatch
): Promise<VideoInfoProcess> {
  await delay(UPDATE_VIDEO_PROGRESS_POLL_INTERVAL)

  const videoInfo = await getVideoProcessingStatus(realEstateId, offerApiId)
  dispatch(updateVideoInfo({ offerApiId: offerApiId, videoInfo: videoInfo }))
  if (videoInfo.videoInfo?.processingStatus === 'SUCCESSFUL' || videoInfo.videoInfo?.processingStatus === 'FAILED') {
    return videoInfo
  }
  return loopVideoProcessingStatus(realEstateId, offerApiId, dispatch)
}

export const loadAndUpdateVideoInfo = (realEstateId: number, offerApiId: string) => async (dispatch: AppDispatch) => {
  try {
    const videoInfo = await getVideoProcessingStatus(realEstateId, offerApiId)
    dispatch(updateVideoInfo({ offerApiId: offerApiId, videoInfo: videoInfo }))
  } catch (error) {
    getDefaultLogger().warn('Updating video info failed!', error)
  }
}

function delay(time: number) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(null), time)
  })
}
export const editAttachmentName =
  (id: string, name: string, source?: string, size?: number) =>
  async (dispatch: DispatchType, getState: () => InitState) => {
    try {
      const requiresMultimediaOperation = getState().insertionFeature.insertionIdentification.realEstateId !== undefined
      !requiresMultimediaOperation && (await updateNameRequest(id, name, source!, size))
      tracking({
        eventName: 'attachment_edit_title',
        flowInformation: getState().flowFeature.flowInformation,
        entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
      })
      dispatch(insertionChanged())
      dispatch(changeAttachmentTitle({ id: id, title: name, moveToAttachmentToUpdate: requiresMultimediaOperation }))
    } catch (error) {
      dispatch(showMessage({ message: 'unknown' }))
    }
  }

export const updatePicture =
  (id: string, image: Blob, source: string, size?: number) =>
  async (dispatch: DispatchType, getState: () => InitState) => {
    try {
      const requiresMultimediaOperation = getState().insertionFeature.insertionIdentification.realEstateId !== undefined
      const attachment = await updatePictureRequest(id, image, source, size)

      tracking({
        eventName: 'attachment_update_image',
        flowInformation: getState().flowFeature.flowInformation,
        entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
      })
      dispatch(insertionChanged())
      dispatch(
        updatePictureSuccess({ id: id, attachment: attachment, moveToAttachmentsToDelete: requiresMultimediaOperation })
      )
    } catch {
      dispatch(showMessage({ message: 'unknown' }))
    }
  }

export const changeFloorPlanStatus = (id: string) => (dispatch: DispatchType, getState: () => InitState) => {
  const requiresMultimediaOperation = getState().insertionFeature.insertionIdentification.realEstateId !== undefined
  tracking({
    eventName: 'attachment_change_floorplan_status_click',
    flowInformation: getState().flowFeature.flowInformation,
    entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
  })
  dispatch(insertionChanged())
  dispatch(changeFloorplanStatus({ id: id, moveToAttachmentToUpdate: requiresMultimediaOperation }))
}

export const changeImageOrder =
  (activeId: UniqueIdentifier, overId: UniqueIdentifier) => (dispatch: DispatchType, getState: () => InitState) => {
    tracking({
      eventName: 'attachment_change_image_order',
      flowInformation: getState().flowFeature.flowInformation,
      entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
    })
    dispatch(insertionChanged())
    dispatch(changeAttachmentOrder({ activeId: activeId, overId: overId }))
  }

export const setImageOrder = (orderedIds: string[]) => (dispatch: DispatchType, getState: () => InitState) => {
  tracking({
    eventName: 'attachment_change_image_order',
    flowInformation: getState().flowFeature.flowInformation,
    entryMode: getState().insertionFeature.insertionIdentification.metaInformation?.entryMode,
  })
  dispatch(insertionChanged())
  dispatch(setPictureOrder(orderedIds))
}

export const doUploadMultimediaLink = (title: string, linkUrl: string) => async (dispatch: DispatchType) => {
  try {
    const linkAttachment = await uploadMultimediaLink(title, linkUrl)
    dispatch(insertionChanged())
    dispatch(uploadAttachmentLink({ attachment: linkAttachment }))
  } catch (error) {
    dispatch(showMessage({ message: 'unknown' }))
    getDefaultLogger().warn('Upload multimedia link failed!', error)
  }
}
